/**
 * Domain enumerations.
 *
 * These are declared as `const` objects rather than TypeScript `enum`s so the
 * exact same values can be used as PostgreSQL enum members (TypeORM), as Zod
 * literals, and as plain strings in React — with no runtime enum object to
 * keep in sync and no `isolatedModules` friction.
 */
// -----------------------------------------------------------------------------
// Access control
// -----------------------------------------------------------------------------
/**
 * Roles are intentionally few. The client operates with 5–10 internal members,
 * so a deep hierarchy would be overhead. Capability differences live in
 * `ROLE_PERMISSIONS` (see permissions.ts), not in extra role names.
 */
export const Role = {
    /** Bootstrap owner. Can do everything, including managing other admins. */
    SUPER_ADMIN: 'SUPER_ADMIN',
    /** Runs the site: structure, navigation, settings, users, all content. */
    ADMIN: 'ADMIN',
    /** Owns the blog: writes, edits and publishes any post. No site structure. */
    EDITOR: 'EDITOR',
    /** Writes drafts and edits their own work. Cannot publish. */
    AUTHOR: 'AUTHOR',
};
export const ROLES = Object.values(Role);
/** Ordered most- to least-privileged. Used for UI sorting and guard messages. */
export const ROLE_RANK = {
    SUPER_ADMIN: 40,
    ADMIN: 30,
    EDITOR: 20,
    AUTHOR: 10,
};
export const ROLE_LABELS = {
    SUPER_ADMIN: 'Super admin',
    ADMIN: 'Admin',
    EDITOR: 'Editor',
    AUTHOR: 'Author',
};
export const ROLE_DESCRIPTIONS = {
    SUPER_ADMIN: 'Full control, including managing administrators and roles.',
    ADMIN: 'Manages pages, navigation, settings, team members and all content.',
    EDITOR: 'Writes, edits and publishes blog posts. Cannot change site structure.',
    AUTHOR: 'Writes and edits their own drafts. Publishing requires an editor.',
};
// -----------------------------------------------------------------------------
// Content lifecycle
// -----------------------------------------------------------------------------
/**
 * `status` answers "where is this in the editorial workflow?".
 *
 * It is deliberately separate from the `isEnabled` boolean, which answers
 * "should this be served to the public right now?". Keeping them apart lets an
 * admin pull a live post offline instantly without destroying its publication
 * date or forcing it back through review.
 */
export const PostStatus = {
    DRAFT: 'DRAFT',
    IN_REVIEW: 'IN_REVIEW',
    SCHEDULED: 'SCHEDULED',
    PUBLISHED: 'PUBLISHED',
    ARCHIVED: 'ARCHIVED',
};
export const POST_STATUSES = Object.values(PostStatus);
export const POST_STATUS_LABELS = {
    DRAFT: 'Draft',
    IN_REVIEW: 'In review',
    SCHEDULED: 'Scheduled',
    PUBLISHED: 'Published',
    ARCHIVED: 'Archived',
};
// -----------------------------------------------------------------------------
// Site structure
// -----------------------------------------------------------------------------
/**
 * How a page's body is produced.
 *
 * STATIC — rendered by a hand-built React component (the Stitch-designed
 *          marketing pages). The CMS still owns its slug, visibility,
 *          navigation position and SEO metadata, but not its layout.
 * CUSTOM — created by the team in the admin. Body is rich text, rendered
 *          through one shared template. This is what makes "add a new page"
 *          possible without a developer.
 */
export const PageKind = {
    STATIC: 'STATIC',
    CUSTOM: 'CUSTOM',
};
export const PAGE_KINDS = Object.values(PageKind);
export const PageStatus = {
    DRAFT: 'DRAFT',
    PUBLISHED: 'PUBLISHED',
};
export const PAGE_STATUSES = Object.values(PageStatus);
/**
 * Named menus. The footer in the reference design has three independent link
 * columns, so each is its own menu rather than a nested tree — that keeps the
 * admin UI flat and obvious.
 */
export const MenuKey = {
    HEADER: 'HEADER',
    FOOTER_SOLUTIONS: 'FOOTER_SOLUTIONS',
    FOOTER_COMPANY: 'FOOTER_COMPANY',
    FOOTER_LEGAL: 'FOOTER_LEGAL',
};
export const MENU_KEYS = Object.values(MenuKey);
export const MENU_LABELS = {
    HEADER: 'Header navigation',
    FOOTER_SOLUTIONS: 'Footer — Solutions',
    FOOTER_COMPANY: 'Footer — Company',
    FOOTER_LEGAL: 'Footer — Legal',
};
// -----------------------------------------------------------------------------
// Inquiries (contact form)
// -----------------------------------------------------------------------------
export const InquiryStatus = {
    NEW: 'NEW',
    IN_PROGRESS: 'IN_PROGRESS',
    CLOSED: 'CLOSED',
    SPAM: 'SPAM',
};
export const INQUIRY_STATUSES = Object.values(InquiryStatus);
export const INQUIRY_STATUS_LABELS = {
    NEW: 'New',
    IN_PROGRESS: 'In progress',
    CLOSED: 'Closed',
    SPAM: 'Spam',
};
// -----------------------------------------------------------------------------
// Media
// -----------------------------------------------------------------------------
/**
 * Where image bytes physically live.
 *
 * `db` is the current project decision — bytes go into a Postgres `bytea`
 * column. The other drivers exist so the choice can be reversed with a single
 * environment variable if media volume grows; nothing in the application layer
 * knows which driver is active. See docs/ARCHITECTURE.md#media.
 */
export const StorageDriver = {
    DB: 'db',
    LOCAL: 'local',
    S3: 's3',
};
/**
 * Derived renditions generated on upload, so the public site never downloads a
 * 4000px hero to fill a 320px card.
 */
export const MediaVariant = {
    /** 320px wide — admin thumbnails, blog card images on mobile. */
    THUMB: 'thumb',
    /** 800px wide — blog card images, inline body images. */
    MEDIUM: 'medium',
    /** 1600px wide — hero and cover images. */
    LARGE: 'large',
    /** Untouched upload. Served only when explicitly requested. */
    ORIGINAL: 'original',
};
export const MEDIA_VARIANT_WIDTHS = {
    thumb: 320,
    medium: 800,
    large: 1600,
};
// -----------------------------------------------------------------------------
// Audit
// -----------------------------------------------------------------------------
export const AuditAction = {
    CREATE: 'CREATE',
    UPDATE: 'UPDATE',
    DELETE: 'DELETE',
    PUBLISH: 'PUBLISH',
    UNPUBLISH: 'UNPUBLISH',
    REORDER: 'REORDER',
    LOGIN: 'LOGIN',
    LOGIN_FAILED: 'LOGIN_FAILED',
    LOGOUT: 'LOGOUT',
    ROLE_CHANGE: 'ROLE_CHANGE',
    PASSWORD_CHANGE: 'PASSWORD_CHANGE',
    INVITE: 'INVITE',
};
//# sourceMappingURL=enums.js.map